import React from 'react';
import { Accordion } from '@packages/ui/composites';
import { Stack, Inline } from '@packages/ui/layout';
import type { FormEachNode } from '../../types/layout';
import { FormRowExcludedValuesProvider } from '../../context/FormRowContext';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useFormEachArray } from '../../hooks/useFormEachArray';
import { useArrayEffectProcessor } from '../../processors/arrayEffectProcessor';
import { resolveAddButtonPosition } from '../../utils/resolveAddButtonPosition';
import { resolveRowHeader } from '../../utils/eachRowHeader';
import { buildExcludedSiblingValues } from './each/buildExcludedSiblingValues';
import { FormEachAddButton } from './each/FormEachAddButton';
import { FormEachRemoveButton } from './each/FormEachRemoveButton';
import { FormEachResetButton } from './each/FormEachResetButton';
import { FormEachItemFields } from './each/FormEachItemFields';

interface FormEachAccordionRendererProps {
  node: FormEachNode;
}

export const FormEachAccordionRenderer = React.memo(function FormEachAccordionRenderer({
  node,
}: FormEachAccordionRendererProps): React.ReactElement {
  const { form, hydrationEpochRef } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();

  useArrayEffectProcessor({
    form: form as never,
    arrayEffects: node.arrayEffects,
    arrayName: node.name,
    meta,
    getOptionProp,
    hydrationEpochRef,
  });

  const {
    fields,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
    allRowValues,
    siblingExcludeFields,
  } = useFormEachArray(node);

  const columns = typeof node.columns === 'number' || typeof node.columns === 'string'
    ? node.columns
    : (node.columns ?? 2);

  const addPosition = resolveAddButtonPosition(node);
  const addControl = showAdd ? <FormEachAddButton node={node} onClick={handleAdd} /> : null;
  const defaultExpanded = node.defaultCollapsed === true ? '' : fields[0]?.id ?? '';

  return (
    <Stack gap='var(--ui-space-md, 16px)'>
      {addPosition === 'top-right' && addControl != null && (
        <Inline justify='end'>{addControl}</Inline>
      )}

      <Accordion.Root
        type='single'
        defaultValue={defaultExpanded}
        semanticId={`${node.name}Accordion`}>
        {fields.map((item, index) => {
          const excludedValues = buildExcludedSiblingValues(allRowValues, index, siblingExcludeFields);
          const rowValues = allRowValues?.[index] as Record<string, unknown> | undefined;
          const header = resolveRowHeader(node.rowHeader, index, rowValues);
          const showRemove = canRemoveRow();

          return (
            <FormRowExcludedValuesProvider key={item.id} value={excludedValues}>
              <Accordion.Item value={item.id}>
                <Accordion.Trigger>
                  <Inline justify='between' align='center' style={{ width: '100%' }}>
                    <span>{header.label ?? `Item ${index + 1}`}</span>
                    {showRemove && node.removeButton?.position === 'header-right' && (
                      <FormEachRemoveButton node={node} index={index} onRemove={handleRemove} />
                    )}
                  </Inline>
                </Accordion.Trigger>
                <Accordion.Content>
                  <Stack gap='12px'>
                    <FormEachItemFields
                      node={node}
                      index={index}
                      total={fields.length}
                      columns={columns}
                      itemClassName={node.itemClassName}
                    />
                    {(showRemove || node.resetButton != null) && node.removeButton?.position !== 'header-right' && (
                      <Inline justify='end' gap='8px'>
                        {node.resetButton != null && <FormEachResetButton node={node} index={index} />}
                        {showRemove && <FormEachRemoveButton node={node} index={index} onRemove={handleRemove} />}
                      </Inline>
                    )}
                  </Stack>
                </Accordion.Content>
              </Accordion.Item>
            </FormRowExcludedValuesProvider>
          );
        })}
      </Accordion.Root>

      {addPosition !== 'top-right' && addControl}
    </Stack>
  );
});
